import React, { useCallback, useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { isEmpty } from 'lodash'
import { AutoComplete } from 'components/Form'
import { Alert } from 'components'
import { LightButton } from 'styles/GlobalStyle'
import { semesterAndYearOptions } from 'keys/years'
import { getCurrentSchoolYear } from 'utils/general'
import { promoteSelectionFetch } from 'store/middlewares/affiliation.crud'
import { toastConfig } from 'config'
import qc from 'keys/queryClient'
import { SchoolContext } from './SchoolContext'

// "העלאת כיתה" - בחירת שנת הלימודים שאת מצבתה צופים, ולחצן להעלאת מה שנבחר
// (שכבה/כיתה/תלמידים בודדים) לשנת הלימודים הנוכחית בפועל.
const PromoteClassControls = () => {
    const {
        promoteYear, setPromoteYear,
        promoteSelection, setPromoteSelection,
        promoteSelectedGradeId, setPromoteSelectedGradeId,
        classStudentsCache, setClassStudentsCache,
        schoolClassGrades
    } = useContext(SchoolContext)
    const { affiliationID } = useParams()
    const queryClient = useQueryClient()

    const [preview, setPreview] = useState(null) // { existing_classes, missing_classes } כשפתוח - הפופאפ מוצג

    // "שכבה שלמה" נבחרה -> שולחים grade_id בלבד (השרת יפתור בעצמו מי נמצא בה כרגע).
    // אחרת - לכל כיתה: אם היא כולה מסומנת -> class_id, אחרת רק התלמידים שסומנו בה בפועל -> student_ids
    const resolveSelectionPayload = useCallback(() => {
        const grade = schoolClassGrades?.data?.find(g => g.id == promoteSelectedGradeId)
        if (!grade) return null

        if (promoteSelection[grade.id])
            return { grade_id: grade.id }

        const class_ids = []
        const student_ids = []
        grade.classes?.forEach(cls => {
            if (promoteSelection[cls.id]) {
                class_ids.push(cls.id)
            } else {
                const studentsOfClass = classStudentsCache[cls.id] || []
                studentsOfClass.forEach(sid => {
                    if (promoteSelection[sid]) student_ids.push(sid)
                })
            }
        })
        return { class_ids, student_ids }
    }, [schoolClassGrades, promoteSelectedGradeId, promoteSelection, classStudentsCache])

    const { mutateAsync: fetchPreview, isPending: previewLoading } = useMutation({
        mutationFn: (payload) => promoteSelectionFetch(affiliationID, { ...payload, from_school_year: promoteYear, dry_run: true })
    })

    const { mutateAsync: executePromotion, isPending: promoting } = useMutation({
        mutationFn: (payload) => promoteSelectionFetch(affiliationID, { ...payload, from_school_year: promoteYear, dry_run: false }),
        onSuccess: () => {
            toast('התלמידים קודמו בהצלחה', toastConfig)
            setPreview(null)
            setPromoteSelection({})
            setPromoteSelectedGradeId(null)
            setClassStudentsCache({})
            queryClient.invalidateQueries({ queryKey: [qc.affiliationGrades, affiliationID] })
            queryClient.invalidateQueries({ queryKey: [qc.testTakers] })
        },
        onError: ({ response }) => {
            toast.error(response?.data?.detail || response?.data || 'אירעה שגיאה בהעלאת הכיתה', toastConfig)
        }
    })

    const createAndPromoteClass = useCallback(async () => {
        const payload = resolveSelectionPayload()
        if (!payload) return
        const { data } = await fetchPreview(payload)
        setPreview(data)
    }, [resolveSelectionPayload, fetchPreview])

    const confirmPromotion = useCallback(() => {
        const payload = resolveSelectionPayload()
        if (payload) executePromotion(payload)
    }, [resolveSelectionPayload, executePromotion])

    return (
        <>
            <AutoComplete
                hideClear
                size="small"
                placeholder="שנת לימודים"
                options={semesterAndYearOptions}
                input={{
                    value: promoteYear,
                    // ה-onChange של AutoComplete מעביר את אובייקט האופציה כולו ({id, name, label}),
                    // לא רק את ה-id - ושומרים כסקלר (promoteYear משמש גם כ-school_year מספרי בבקשות ל-API)
                    onChange: (val) => setPromoteYear(val?.id ?? val)
                }}
            />
            <LightButton
                size="small"
                label="צור כיתות ושבץ"
                loading={previewLoading}
                disabled={isEmpty(promoteSelection) || promoteYear == getCurrentSchoolYear()}
                action={createAndPromoteClass}
            />
            {preview &&
                <Alert
                    systemUI
                    title="אישור העלאת כיתה"
                    handleAlert={() => setPreview(null)}
                    footerActions={[
                        { label: 'אישור והמשך', variant: 'danger', color: 'error', action: confirmPromotion, loading: promoting },
                        { label: 'ביטול', variant: 'white', color: 'thirdly4', action: () => setPreview(null) }
                    ]}
                >
                    {!isEmpty(preview.existing_classes) &&
                        <div>{`כיתות ${preview.existing_classes.join(', ')} כבר קיימות`}</div>}
                    {!isEmpty(preview.missing_classes) &&
                        <div>{`כיתות ${preview.missing_classes.join(', ')} לא קיימות ויווצרו כעת`}</div>}
                </Alert>}
        </>
    )
}

export default PromoteClassControls
